import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import './ComboBoxTree.less';
import debounce from 'debounce';
import { ComboBox } from 'components/inputs/ComboBox';
import { EpickTree, IEpickNode } from 'components/EntityPicker/EpickTree';
import { IEntityPickerTreeModel } from 'components/EntityPicker/defaultEpickTreeModel';
import { ValidationSummary } from 'utils/ValidationUtils';
import { DecisionsTypePickerSettings } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/DecisionsTypePickerSettings';
import { generateGuid } from 'utils/Guid';

export interface IMoreItem {
  id: string;
  title: string;
  onSelected: (settings?: DecisionsTypePickerSettings | null) => void;
}

export interface IComboBoxTreeProps {
  hideClear?: boolean;
  validation?: ValidationSummary;
  createModel: (searchValue?: string) => IEntityPickerTreeModel | null;
  onSelected: (item: IEpickNode | null) => void;
  onSearch?: (search: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /**
   * Actions which will be shown in the drop down below the suggested types
   */
  moreItems?: IMoreItem[];
  callOutHeight?: number | 'auto';
  searchValue: string;
  handleSearch?: boolean;
  settings?: DecisionsTypePickerSettings | null;
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ComboBoxTree({
  hideClear,
  createModel,
  moreItems,
  onSelected,
  onSearch,
  onFocus,
  callOutHeight = 'auto',
  validation,
  searchValue: _searchValue,
  handleSearch = true,
  settings,
}: IComboBoxTreeProps) {
  const comboBoxRef = useRef<HTMLDivElement | null>(null);
  const [layerId] = useState(`type-picker-${generateGuid()}`);
  const [model, setModel] = useState<IEntityPickerTreeModel | null>(null);
  const [searchValue, setSearchValue] = useState<string>(_searchValue);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);

  useEffect(() => {
    setSearchValue(_searchValue);
    setIsSearch(false);
  }, [_searchValue]);

  useEffect(() => {
    // if layer is opened and factory-method to create model was changed we need to re-create model to display correct tree.
    // It is primarily required for tests, as after upgrade to react 18 (something was changed around useEffect https://reactjs.org/blog/2022/03/08/react-18-upgrade-guide.html#react),
    // there is a race condition, between loading settings in TypeComboPicker inside useEffect and creating model inside ComboBoxTree when layer is shown:
    // Issue description
    // 1. Render component
    // 2. Request to get settings it triggered on initial render
    // 3. Click on open layer button
    // 4. createModel method is triggered (createModel still use old settings, they are null yet) => model is null => empty layer is shown
    // 5. createModel started to use updated settings, but layer empty layer is shown already
    //
    if (isOpen) {
      const model = createModel(searchValue);
      setModel(model);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModel]);

  const handleOpen = useCallback(
    async (searchValue?: string) => {
      setIsOpen(true);
      const model = createModel(searchValue);
      setModel(model);
    },
    [createModel]
  );

  const onClear = useCallback(() => {
    onSelected(null);
    setModel(null);
    setSearchValue('');
  }, [onSelected]);

  const handleSearchChange = useMemo(
    () =>
      debounce(async (value: string) => {
        if (handleSearch) {
          setIsSearch(true);
          await handleOpen(value);
        }
      }, 500),
    [handleOpen, handleSearch]
  );

  const onFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleSearchChange.clear();
      const value = e.target.value;
      setSearchValue(value);
      onSearch?.(value);
      if (value.length > 0) {
        handleSearchChange(value);
      } else {
        handleOpen();
      }
    },
    [handleOpen, handleSearchChange, onSearch]
  );

  const handleNodeSelected = useCallback(
    async (item: IEpickNode) => {
      if (!item || !item.pickable) {
        return;
      }
      const foundItems = (moreItems ?? []).filter(n => n.id === item.id);
      if (foundItems && foundItems.length) {
        foundItems[0].onSelected(settings);
        setIsOpen(false);
        return;
      }
      onSelected(item);
      setIsOpen(false);
      // select input after picking entity
      if (comboBoxRef.current != null) {
        (comboBoxRef.current.querySelector('.dpc-combo-box__input') as HTMLInputElement)?.focus();
      }
    },
    [moreItems, onSelected, settings]
  );

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          !isOpen && handleOpen();
          break;
        case 'Escape':
          setIsOpen(false);
          break;
      }
    },
    [handleOpen, isOpen]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.stopPropagation();
      } else if (e.key === 'ArrowDown') {
        (document.querySelector(`#${layerId} .dpc-tree-node`) as HTMLInputElement)?.focus();
        e.stopPropagation();
        // perenting report row focus event
        e.preventDefault();
      }
    },
    [isOpen, layerId]
  );

  const handleNodeKeyDown = useCallback((_: any, e: React.KeyboardEvent) => {
    if (
      e.key === 'ArrowUp' &&
      (e.target as HTMLElement)?.classList?.contains('dpc-tree-node--first') &&
      ((e.target as HTMLElement)?.parentNode as HTMLElement)?.classList.contains('dpc-epick-tree')
    ) {
      (comboBoxRef.current?.querySelector('.dpc-combo-box__input') as HTMLInputElement)?.focus();
    }
  }, []);

  const onBlur = useCallback(
    (e: React.FocusEvent) => {
      if (!e.relatedTarget?.closest(`#${layerId}`)) {
        setIsOpen(false);
      }
    },
    [layerId]
  );

  const controlProps = {
    onClose: () => setIsOpen(false),
    onOpenClick: () => !isOpen && handleOpen(isSearch ? searchValue : undefined),
    open: isOpen,
  };
  return (
    <ComboBox
      ref={comboBoxRef}
      value={searchValue}
      onChange={onFieldChange}
      onClear={() => {
        onClear();
        setIsOpen(false);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      hideClear={hideClear}
      controlProps={controlProps}
      onKeyUp={handleKeyUp}
      onKeyDown={handleKeyDown}
      validation={validation}
      layerProps={{
        className: 'dpc-combo-box-tree-layer',
        id: layerId,
        flipBehavior: ['bottom', 'top'],
        callOutHeight,
        tabIndex: -1,
      }}
    >
      {model ? (
        <EpickTree epickTreeModel={model} onNodeSelected={handleNodeSelected} onNodeKeyDown={handleNodeKeyDown} />
      ) : null}
    </ComboBox>
  );
}
